import { useEffect, useState } from "react";
import axios from "axios";
import { DataCard } from "src/components/DataCard";
import useChangeTitle from "src/api/hooks/useChangeTitle";
import { Modal } from "src/components/Modal";
import { useForm } from "src/api/hooks/useForm";
import { ItemDescription } from "src/components/ItemDescription";
import { lang } from "src/language/Strings";
import { Button } from "src/components/Buttons/Button";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useGraphToken } from "src/api/hooks/Graph/useGraphToken";
import { useEntraUsers } from "src/api/hooks/Graph/useEntraUsers";
import { authManager } from "src/main";
async function getBuckets(
  accessToken: string,
  groupId: string,
  planId: string
) {
  const buckets = await axios.get(
    `https://graph.microsoft.com/v1.0/groups/${groupId}/planner/plans/${planId}/buckets`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
  return buckets.data.value;
}
function useBuckets() {
  const { data: graphToken } = useGraphToken();
  const { entraGroupId } = useParams();
  const { data: plans } = usePlans();
  return useQuery({
    queryKey: [
      "buckets",
      entraGroupId,
      plans?.[0]?.plan.id ?? "43a85ea2-3435-499b-b306-5c31f6431e43",
    ],
    queryFn: () =>
      getBuckets(
        String(graphToken),
        entraGroupId ?? "43a85ea2-3435-499b-b306-5c31f6431e43",
        plans?.[0]?.plan.id ?? ""
      ),
  });
}

async function getPlans(groupId: string, accessToken: string, users: any[]) {
  if (!accessToken) {
    throw new Error("No access token"); // TODO: Handle this better
  }
  const plans = await axios.get(
    `https://graph.microsoft.com/v1.0/groups/${groupId}/planner/plans`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );

  const plansWithTasks = (
    await Promise.all(
      plans.data.value.map(async (plan) => ({
        plan: plan,
        tasks: (
          await axios.get(
            `https://graph.microsoft.com/v1.0/groups/${groupId}/planner/plans/${plan.id}/tasks?$select=*&$expand=*`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          )
        ).data.value,
        buckets: await getBuckets(accessToken!, groupId, plan.id),
      }))
    )
  )
    .filter((p) => p.tasks.length > 0)
    .map((p) => {
      p.tasks = p.tasks
        .map((t) => {
          t.assignments = Object.entries(t.assignments).map(
            ([key, value]: [string, any]) => {
              return {
                ...value,
                userId: key,
                user: users.find((u) => u.id === key),
              };
            }
          );
          return t;
        })
        .filter(
          (t) =>
            t.assignments.some(
              (a) =>
                a.user.id === authManager.providerLoginResult.idTokenClaims.oid
            ) && t.percentComplete < 100
        );
      return p;
    });
  return plansWithTasks;
}
const usePlans = () => {
  const { data: graphToken } = useGraphToken();
  const { entraGroupId } = useParams();
  const { data: users } = useEntraUsers();
  return useQuery({
    queryKey: [
      "plans",
      entraGroupId ?? "43a85ea2-3435-499b-b306-5c31f6431e43",
      users?.length,
    ],
    queryFn: () =>
      getPlans(
        entraGroupId ?? "43a85ea2-3435-499b-b306-5c31f6431e43",
        String(graphToken),
        users
      ),
  });
};

export function PlannerPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { entraGroupId } = useParams();
  // create a checkbox group for the buckets
  const [selectedBuckets, setSelectedBuckets] = useState<string[]>([]);
  const { data: plans } = usePlans();
  useChangeTitle(plans?.[0]?.plan.title ?? "Uppgifter");
  useEffect(() => {
    setSelectedBuckets(
      plans?.flatMap((p) => p.buckets.map((b) => b.name)) ?? []
    );
  }, [plans]);
  useEffect(() => {
    const el = document.getElementById("plus-icon-add");
    const listener = () => {
      setIsModalOpen(true);
    };
    if (el) {
      el.addEventListener("click", listener);
    }
    return () => {
      el?.removeEventListener("click", listener);
    };
  }, []);
  return (
    <div>
      {plans?.map((p) => (
        <div key={p.plan.id}>
          <div className="flex flex-row gap-2 w-full items-center justify-center">
            {p.buckets.map((b, index) => (
              <div key={`bucket-${b.id}-${index}`}>
                <input
                  id={`bucket-${b.id}-${index}`}
                  type="checkbox"
                  name={b.name}
                  className="w-6 h-6 ml-2"
                  checked={selectedBuckets.includes(b.name)}
                  onChange={(e) => {
                    e.target.checked
                      ? setSelectedBuckets((prev) => [...prev, e.target.name])
                      : setSelectedBuckets((prev) =>
                          prev.filter((b) => b !== e.target.name)
                        );
                  }}
                />
                <label className="text-lg" key={`bucket-${b.id}-${index}`}>
                  {b.name}
                </label>
              </div>
            ))}
          </div>
          <li>
            {p.tasks
              .filter((t) =>
                selectedBuckets.includes(
                  p.buckets.find((b) => b.id === t.bucketId)?.name
                )
              )
              .map((t, index: number) => (
                <DataCard
                  id={`task-${t.id}`}
                  key={`task-${t.id}-${index}`}
                  header={t.title}
                  firstRowInput={t.description}
                  secondRowInput={t.assignments
                    .map((a) => a.user.displayName)
                    .join(", ")}
                  thirdRowInput={
                    p.buckets.find((b) => b.id === t.bucketId)?.name
                  }
                  onClick={() => {}}
                  isSelected={false}
                />
              ))}
          </li>
        </div>
      ))}
      {isModalOpen && (
        <AddTaskModal
          setIsModalOpen={setIsModalOpen}
          entraGroupId={entraGroupId ?? "43a85ea2-3435-499b-b306-5c31f6431e43"}
          planId={plans?.[0]?.plan.id ?? ""}
        />
      )}
    </div>
  );
}

const AddTaskModal = ({
  setIsModalOpen,
  task,
  entraGroupId,
  planId,
}: {
  setIsModalOpen: (isModalOpen: boolean) => void;
  task?: any;
  entraGroupId: string;
  planId: string;
}) => {
  const { data: graphToken } = useGraphToken();
  const { data: plans } = usePlans();
  const { Form, formData, setFormData, resetForm } = useForm(
    task || {
      company: "",
      taskTitle: "",
      appType: "",
      description: "",
      assignedToUserId: authManager.providerLoginResult.idTokenClaims.oid,
      bucketId: "",
      dueDate: new Date(),
      contact: "",
    }
  );
  const buckets = useBuckets();
  return (
    <Modal onOutsideClick={() => setIsModalOpen(false)}>
      <h1 className="text-lg font-bold text-PrimaryGreen text-center">
        Lägg till uppgift
      </h1>
      <Form id="addTaskForm">
        <ItemDescription label={lang.companyName}>
          <Form.Input name="company" defaultValue={formData.company} />
        </ItemDescription>
        <ItemDescription label={lang.taskTitle}>
          <Form.Input name="taskTitle" defaultValue={formData.taskTitle} />
        </ItemDescription>
        <ItemDescription label={lang.appType}>
          <Form.Input name="appType" defaultValue={formData.appType} />
        </ItemDescription>
        <ItemDescription label={lang.contact}>
          <Form.Input
            name="contact"
            defaultValue={formData.contact}
            type="email"
          />
        </ItemDescription>
        <ItemDescription label={lang.bucket}>
          <Form.Select
            name="bucketId"
            defaultValue={buckets.data?.find((b) => b).id}
          >
            {buckets.data?.map((b) => (
              <Form.Select.Option key={b.id} value={b.id}>
                {b.name}
              </Form.Select.Option>
            ))}
          </Form.Select>
        </ItemDescription>
        <ItemDescription label={lang.description}>
          <Form.TextArea
            name="description"
            defaultValue={formData.description}
          />
        </ItemDescription>
        <ItemDescription label={lang.dueDate}>
          <Form.DateInput name="dueDate" defaultValue={formData.dueDate} />
        </ItemDescription>
      </Form>
      <div className="flex flex-row gap-2 justify-center">
        <Button
          className="bg-PrimaryGreen text-White rounded-lg p-2 border-2"
          onClick={() => setIsModalOpen(false)}
          title={lang.close}
          buttonType="button"
        />
        <Button
          className="bg-PrimaryGreen text-White rounded-lg p-2 border-2"
          onClick={async () => {
            const payload = {
              planId: planId,
              bucketId: formData.bucketId,
              title: formData.taskTitle,
              assignments: {
                [authManager.providerLoginResult.idTokenClaims.oid]: {
                  "@odata.type": "#microsoft.graph.plannerAssignment",
                  orderHint: " !",
                },
              },
            };
            await postTask(payload, String(graphToken), entraGroupId, planId);

            setIsModalOpen(false);

            resetForm();
          }}
          title={lang.create}
          buttonType="submit"
          formId="addTaskForm"
        />
      </div>
    </Modal>
  );
};

async function postTask(
  data: any,
  graphToken: string,
  entraGroupId: string,
  planId: string
) {
  const response = await axios.post(
    `https://graph.microsoft.com/v1.0/planner/tasks`,
    data,
    {
      headers: {
        Authorization: `Bearer ${graphToken}`,
      },
    }
  );
  const detailsPayload = {
    previewType: "noPreview",
    checklist: {
      "95e27074-6c4a-447a-aa24-9d718a0b86fa": {
        "@odata.type": "microsoft.graph.plannerChecklistItem",
        title: "Ärendet avslutat",
        isChecked: true,
      },
      "d280ed1a-9f6b-4f9c-a962-fb4d00dc50ff": {
        "@odata.type": "microsoft.graph.plannerChecklistItem",
        title: "Uppföljning inbokat",
        isChecked: true,
      },
    },
  };
  const updatedDetails = await axios.patch(
    `https://graph.microsoft.com/v1.0/planner/${entraGroupId}/planner/plans/${planId}/tasks/${response.data.id}/details`,
    detailsPayload,
    {
      headers: {
        Authorization: `Bearer ${graphToken}`,
      },
    }
  );
  return response.data;
}
